/**
 * Deep link dedup — sessionStorage 기반.
 *
 * 같은 deep link URL 이 짧은 시간 안에 두 번 이상 dispatch 되는 걸 막는다.
 * Capacitor 8 + iOS 환경에서 deep link 가 중복 발화되는 케이스:
 *
 *   1. iOS 가 같은 URL 에 대해 `appUrlOpen` 이벤트를 두 번 발화 (간헐적 OS
 *      버그).
 *   2. Cold launch 시 `App.getLaunchUrl()` 와 `appUrlOpen` 이 동시 발화 —
 *      두 가지 경로로 같은 URL 이 들어옴.
 *   3. Deep link 핸들러가 `window.location.href = ...` 로 full page reload
 *      를 일으키면 layout 이 remount → CapacitorBootstrap 이 다시 mount →
 *      `App.getLaunchUrl()` 가 또 호출되어 OS 가 보관 중인 동일 launch URL
 *      이 다시 dispatch 됨.
 *   4. Safari 탭에서 사용자가 smart banner "열기" 를 누르면 같은 deep link
 *      이 또 발화.
 *
 * (1)(2)는 module-scope Map 으로도 처리 가능하지만 (3) 때문에 storage 가
 * page reload 를 살아남아야 함 → sessionStorage. (4) 도 같은 sessionStorage
 * key 가 살아 있는 한 자동으로 막힘.
 *
 * sessionStorage 는 Safari/WKWebView 탭(또는 Capacitor WebView 인스턴스)
 * 별 격리. 앱 cold boot 으로 process 가 다시 뜨면 자동 초기화. 즉 의도된
 * "새 인증 시도" 는 자연스럽게 새 키로 동작.
 */

const DEDUP_STORAGE_KEY = "locarora_deeplink_dedup";
const DEDUP_TTL_MS = 10 * 60 * 1000;

/**
 * URL 을 dedup store 에 기록하고, 이미 기록돼 있었는지 반환.
 *
 * 반환:
 *   true  → 같은 URL 이 TTL window 안에 이미 처리됨 (caller 는 무시해야 함)
 *   false → 새 URL (caller 가 정상 처리하면 됨)
 *
 * SSR / sessionStorage unavailable 환경에선 항상 false (= 무조건 처리)
 * 반환. dedup 은 best-effort 이고 storage 가 없는 환경은 어차피 native
 * 도 아닐 가능성이 높음.
 */
export function isDuplicateDeepLink(url: string): boolean {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return false;
  }

  const now = Date.now();
  let entries: Record<string, number> = {};
  try {
    const raw = window.sessionStorage.getItem(DEDUP_STORAGE_KEY);
    if (raw) entries = JSON.parse(raw) as Record<string, number>;
  } catch {
    entries = {};
  }

  // Expire stale entries. 10분 지난 항목은 새 attempt 로 간주.
  for (const k of Object.keys(entries)) {
    if (now - entries[k] > DEDUP_TTL_MS) delete entries[k];
  }

  const wasSeen = Object.prototype.hasOwnProperty.call(entries, url);
  entries[url] = now;

  try {
    window.sessionStorage.setItem(
      DEDUP_STORAGE_KEY,
      JSON.stringify(entries),
    );
  } catch {
    // sessionStorage 가득 찼거나 private mode 거부 — dedup 실패해도
    // caller 는 정상 진행하도록 false 반환은 못 하지만, 일단 wasSeen 만
    // 정확히 돌려주면 동기 보장.
  }

  return wasSeen;
}
